import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CalendarDays, ChevronLeft, ChevronRight, MoonStar } from "lucide-react";
import { Lunar, Solar } from "../../services/lunar.js";
import { buildTimeLuckSummary } from "./_helper_zeri.jsx";

const formatDateValue = (date) => {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

const parseDateValue = (value) => {
	if (!value) return new Date();
	const [y, m, d] = value.split("-").map(Number);
	if (!y || !m || !d) return new Date();
	return new Date(y, m - 1, d);
};

const WEEK_LABELS = ["日", "一", "二", "三", "四", "五", "六"];
const ZODIAC_OPTIONS = ["鼠", "牛", "虎", "兔", "龙", "蛇", "马", "羊", "猴", "鸡", "狗", "猪"];

const getMonthMatrix = (year, month) => {
	const firstDay = new Date(year, month, 1);
	const startWeekday = firstDay.getDay();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const daysInPrevMonth = new Date(year, month, 0).getDate();
	const cells = [];

	for (let i = 0; i < 42; i += 1) {
		const dayIndex = i - startWeekday + 1;
		let cellDate;
		let inMonth = true;

		if (dayIndex < 1) {
			cellDate = new Date(year, month - 1, daysInPrevMonth + dayIndex);
			inMonth = false;
		} else if (dayIndex > daysInMonth) {
			cellDate = new Date(year, month + 1, dayIndex - daysInMonth);
			inMonth = false;
		} else {
			cellDate = new Date(year, month, dayIndex);
		}

		cells.push({ date: cellDate, inMonth });
	}

	const weeks = [];
	for (let i = 0; i < cells.length; i += 7) {
		weeks.push(cells.slice(i, i + 7));
	}

	const lastWeek = weeks[weeks.length - 1] || [];
	const hasInMonth = lastWeek.some((cell) => cell.inMonth);
	if (!hasInMonth) {
		weeks.pop();
	}

	return weeks;
};

const getDateRangeValues = (startValue, endValue) => {
	const start = parseDateValue(startValue);
	const end = parseDateValue(endValue);
	const from = start.getTime() <= end.getTime() ? start : end;
	const to = start.getTime() <= end.getTime() ? end : start;
	const list = [];
	const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());

	while (cursor.getTime() <= to.getTime()) {
		list.push(formatDateValue(cursor));
		cursor.setDate(cursor.getDate() + 1);
	}

	return list;
};

const ZeriMobile = () => {
	const navigate = useNavigate();
	const [dateValue, setDateValue] = useState(formatDateValue(new Date()));
	const [selectedDateValues, setSelectedDateValues] = useState([formatDateValue(new Date())]);
	const [viewYear, setViewYear] = useState(new Date().getFullYear());
	const [viewMonth, setViewMonth] = useState(new Date().getMonth());
	const [divinationType, setDivinationType] = useState("通用");
	const [isDivinationOpen, setIsDivinationOpen] = useState(false);
	const [timePeriod, setTimePeriod] = useState("");
	const [enableMultiDayRun, setEnableMultiDayRun] = useState(false);
	const [rangeStartValue, setRangeStartValue] = useState("");
	const [isTimePeriodOpen, setIsTimePeriodOpen] = useState(false);
	const [zodiac, setZodiac] = useState("");
	const [showAvoidTimes, setShowAvoidTimes] = useState(false);
	const [showRecommendations, setShowRecommendations] = useState(false);

	const selectedDate = useMemo(() => parseDateValue(dateValue), [dateValue]);
	const todayValue = useMemo(() => formatDateValue(new Date()), []);

	useEffect(() => {
		// 当 selectedDate 更改时，不要强制重置 viewYear/viewMonth
		// 只有在手动切换月份时才更新 viewYear/viewMonth
		// setViewYear(selectedDate.getFullYear());
		// setViewMonth(selectedDate.getMonth());
	}, [selectedDate]);

	const solar = useMemo(() => {
		return Solar.fromYmd(
			selectedDate.getFullYear(),
			selectedDate.getMonth() + 1,
			selectedDate.getDate()
		);
	}, [selectedDate]);

	const lunar = useMemo(() => Lunar.fromDate(selectedDate), [selectedDate]);

	const timeLuck = useMemo(() => {
		return buildTimeLuckSummary(selectedDate, zodiac, divinationType, timePeriod);
	}, [selectedDate, zodiac, divinationType, timePeriod]);

	const multiDayResults = useMemo(() => {
		const dateList = enableMultiDayRun ? selectedDateValues : [dateValue];
		return dateList
			.map((value) => {
				const date = parseDateValue(value);
				const dayLunar = Lunar.fromDate(date);
				return {
					value,
					date,
					label: `${value} 周${Solar.fromYmd(
						date.getFullYear(),
						date.getMonth() + 1,
						date.getDate()
					).getWeekInChinese()}`,
					lunarLabel: `${dayLunar.getMonthInChinese()}月${dayLunar.getDayInChinese()}`,
					summary: buildTimeLuckSummary(date, zodiac, divinationType, timePeriod),
				};
			})
			.sort((a, b) => a.date.getTime() - b.date.getTime());
	}, [enableMultiDayRun, selectedDateValues, dateValue, zodiac, divinationType, timePeriod]);

	const sortedSelectedDateValues = useMemo(
		() => selectedDateValues.slice().sort(),
		[selectedDateValues]
	);
	const rangeStartDisplay = sortedSelectedDateValues[0] || "--";
	const rangeEndDisplay = sortedSelectedDateValues[sortedSelectedDateValues.length - 1] || "--";
	const zodiacSliderValue = zodiac ? ZODIAC_OPTIONS.indexOf(zodiac) + 1 : 0;

	const headerInfo = useMemo(() => {
		const weekday = `周${solar.getWeekInChinese()}`;
		const lunarLabel = `${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}`;
		return {
			weekday,
			lunarLabel,
			dayGanzhi: lunar.getDayInGanZhi(),
			monthGanzhi: lunar.getMonthInGanZhi(),
			yearGanzhi: lunar.getYearInGanZhi(),
		};
	}, [selectedDate, lunar]);

	const summary = useMemo(() => {
		const source = enableMultiDayRun
			? multiDayResults.reduce(
				(acc, item) => {
					acc.good += item.summary.goodTimes.length;
					acc.bad += item.summary.badTimes.length;
					return acc;
				},
				{ good: 0, bad: 0 }
			)
			: {
				good: timeLuck.goodTimes.length,
				bad: timeLuck.badTimes.length,
			};
		return {
			goodCount: source.good,
			badCount: source.bad,
		};
	}, [enableMultiDayRun, multiDayResults, timeLuck]);

	const dayDetails = useMemo(() => {
		return {
			naYin: lunar.getDayNaYin(),
			xunKong: lunar.getDayXunKong(),
			chongSha: `${lunar.getDayChongDesc()} ${lunar.getDaySha()}`,
			xiShen: lunar.getDayPositionXiDesc(),
			caiShen: lunar.getDayPositionCaiDesc(),
		};
	}, [lunar]);

	const calendarWeeks = useMemo(() => getMonthMatrix(viewYear, viewMonth), [viewYear, viewMonth]);

	useEffect(() => {
		if (!enableMultiDayRun) {
			setSelectedDateValues([dateValue]);
			setRangeStartValue("");
		}
	}, [enableMultiDayRun, dateValue]);

	useEffect(() => {
		if (!enableMultiDayRun) return;
		setSelectedDateValues([dateValue]);
		setRangeStartValue("");
	}, [enableMultiDayRun]);

	const isDatePicked = (value) => {
		if (enableMultiDayRun) return selectedDateValues.includes(value);
		return value === dateValue;
	};

	const handleSelectDate = (date) => {
		const targetValue = formatDateValue(date);
		setDateValue(targetValue);

		if (enableMultiDayRun) {
			if (!rangeStartValue) {
				setRangeStartValue(targetValue);
				setSelectedDateValues([targetValue]);
			} else {
				setSelectedDateValues(getDateRangeValues(rangeStartValue, targetValue));
				setRangeStartValue("");
			}
		}

		setViewYear(date.getFullYear());
		setViewMonth(date.getMonth());
	};

	const handlePrevMonth = () => {
		const prev = new Date(viewYear, viewMonth - 1, 1);
		setViewYear(prev.getFullYear());
		setViewMonth(prev.getMonth());
	};

	const handleNextMonth = () => {
		const next = new Date(viewYear, viewMonth + 1, 1);
		setViewYear(next.getFullYear());
		setViewMonth(next.getMonth());
	};

	return (
		<div
			className="min-h-screen text-slate-900"
			style={{
				fontFamily: "'ZCOOL XiaoWei', 'Noto Serif SC', 'Songti SC', serif",
				background: "radial-gradient(circle at top, #fef3c7 0%, #f7efe6 45%, #eef2ff 100%)",
			}}
		>
			<Helmet>
				<title>圆運-奇门择日</title>
				<meta
					name="description"
					content="输入属相、选择办事类型，给出吉日吉时。"
				/>

				<meta property="og:url" content="https://bazi.tryonebest.com/zeri/mobile" />

				<meta property="og:site_name" content="圆運易学" />
			</Helmet>
			<div className="relative overflow-hidden">
				<div className="pointer-events-none absolute -top-20 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-[#fde68a] blur-3xl opacity-70 animate-blob" />
				<div className="pointer-events-none absolute -right-10 top-16 h-56 w-56 rounded-full bg-[#c7f9cc] blur-3xl opacity-60 animate-blob animation-delay-2000" />
				<div className="pointer-events-none absolute bottom-0 left-6 h-64 w-64 rounded-full bg-[#c7d2fe] blur-3xl opacity-50 animate-blob animation-delay-4000" />

				<div className="relative mx-auto flex max-w-sm flex-col px-4 pb-16 pt-6">
					<section className="mt-1 rounded-[28px] border border-white/70 bg-white/80 p-4 shadow-xl shadow-amber-100/50">
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={handlePrevMonth}
								className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none"
								aria-label="上个月"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
							<div className="text-sm font-semibold text-slate-900">
								{viewYear}年 {viewMonth + 1}月
							</div>
							<button
								type="button"
								onClick={handleNextMonth}
								className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none"
								aria-label="下个月"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>

						<div className="mt-2 grid grid-cols-7 gap-1 rounded-xl bg-white/70 px-1 text-center text-[11px] text-slate-500">
							{WEEK_LABELS.map((label) => (
								<div key={label} className="py-1 font-semibold">
									{label}
								</div>
							))}
						</div>

						<div className="mt-1 rounded-2xl border border-slate-200 bg-white/70 p-2 shadow-sm">
							<div className="grid gap-2">
								{calendarWeeks.map((week, weekIndex) => (
									<div key={`week-${weekIndex}`} className="grid grid-cols-7 gap-1">
										{week.map((cell) => {
											const cellValue = formatDateValue(cell.date);
											const isSelected = isDatePicked(cellValue);
											const isToday = cellValue === todayValue;
											const lunarText = Lunar.fromDate(cell.date).getDayInChinese();

											// 状态样式区分
											let cellStateClasses = "";
											if (isSelected) {
												// 选中状态：强制琥珀色背景和白色文字 (去除 hover 灰色干扰)
												cellStateClasses = "border-amber-500 bg-amber-500 text-white shadow-sm";
											} else {
												// 未选中状态：区分当前月和非当前月
												const baseText = cell.inMonth ? "text-slate-900" : "text-slate-400";
												const baseSurface = cell.inMonth ? "bg-white" : "bg-slate-50";
												const baseBorder = cell.inMonth ? "border-slate-200" : "border-slate-100";
												// 仅在未选中时应用 hover 效果
												cellStateClasses = `${baseText} ${baseSurface} ${baseBorder} hover:border-slate-300 hover:bg-slate-50`;
											}

											return (
												<button
													key={cellValue}
													type="button"
													onClick={() => handleSelectDate(cell.date)}
													className={`flex h-[54px] min-h-[44px] flex-col items-center justify-center rounded-md border px-1 text-center text-xs transition-colors duration-200 motion-reduce:transition-none ${cellStateClasses} cursor-pointer`}
												>
													<div className="text-sm font-semibold">{cell.date.getDate()}</div>
													<div
														className={`mt-1 text-[10px] ${isSelected ? "text-white/80" : "text-slate-500"}`}
													>
														{lunarText}
													</div>
												</button>
											);
										})}
									</div>
								))}
							</div>
						</div>
						{/* 被选中日期 */}
						<div className="mt-1 rounded-2xl bg-[#f8fafc] px-6 py-2 text-center">
							<div className="text-lg font-bold text-slate-900">
								{enableMultiDayRun
									? `已选择 ${selectedDateValues.length} 天`
									: `${formatDateValue(selectedDate)} ${headerInfo.weekday}`}
							</div>
							{!enableMultiDayRun && (
								<div className="mt-1 text-xs text-slate-600">
									农历 {headerInfo.lunarLabel} · {headerInfo.yearGanzhi}年 {headerInfo.monthGanzhi}月 {headerInfo.dayGanzhi}日
								</div>
							)}
							{enableMultiDayRun && (
								<>

									<div className="mt-1 rounded-lg bg-white/70 px-3 py-2 text-center text-xs text-slate-700">
										<div>范围：{rangeStartDisplay}日--{rangeEndDisplay}日</div>
									</div>
								</>
							)}
							{!enableMultiDayRun && (
								<div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-700">
									<div className="rounded-md border border-emerald-100 bg-emerald-50/80 px-2 py-1">
										<span className="font-semibold text-emerald-700">喜神方</span> {dayDetails.xiShen}
									</div>
									<div className="rounded-md border border-orange-100 bg-orange-50/80 px-2 py-1">
										<span className="font-semibold text-orange-700">财神方</span> {dayDetails.caiShen}
									</div>
								</div>
							)}
						</div>
					</section>

					<section className="-order-1 mt-4 rounded-3xl border border-white/60 bg-white/80 p-4 shadow-lg shadow-amber-100/40">
						<details className="group">
							<summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-slate-900">
								择日类型
								<span className="text-xs font-semibold text-slate-500 transition group-open:rotate-180">
									▾
								</span>
							</summary>
							<div className="mt-3 grid gap-3 text-xs text-slate-700">
								<div className="grid gap-2">
									<div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-2">
										<span className="min-w-[42px] whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-center text-xs font-semibold text-amber-700">
											{zodiac || "属相"}
										</span>
										<div className="flex-1">
											<input
												type="range"
												min={0}
												max={ZODIAC_OPTIONS.length}
												step={1}
												value={zodiacSliderValue}
												onChange={(e) => {
													const next = Number(e.target.value);
													setZodiac(next === 0 ? "" : ZODIAC_OPTIONS[next - 1]);
												}}
												className="h-2 w-full accent-amber-500"
												aria-label="属相滑动选择"
											/>
										</div>
										
									</div>
									<div className="text-[11px] text-slate-500">拖动拉杆切换十二属相</div>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div className="grid gap-1">
										<span className="font-semibold text-slate-600">求占类型</span>
										<div className="relative">
											<button
												type="button"
												className="flex h-10 w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:border-amber-300"
												aria-haspopup="listbox"
												aria-expanded={isDivinationOpen}
												onClick={() => setIsDivinationOpen((prev) => !prev)}
											>
												<span>{divinationType}</span>
												<span className="text-xs text-slate-500">▾</span>
											</button>
											{isDivinationOpen && (
												<div className="absolute left-0 right-0 z-10 mt-2 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
													{["通用", "搬家", "结婚", "打牌求财"].map((option) => (
														<button
															key={option}
															type="button"
															className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${divinationType === option
																? "bg-amber-50 text-amber-700"
																: "text-slate-700 hover:bg-slate-50"
																}`}
															onClick={() => {
																setDivinationType(option);
																setIsDivinationOpen(false);
															}}
														>
															<span>{option}</span>
															{divinationType === option && (
																<span className="text-xs font-semibold">✓</span>
															)}
														</button>
													))}
												</div>
											)}
										</div>
									</div>
									<div className="grid gap-1">
										<span className="font-semibold text-slate-600">时段选择</span>
										<div className="relative">
											<button
												type="button"
												className="flex h-10 w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:border-amber-300"
												aria-haspopup="listbox"
												aria-expanded={isTimePeriodOpen}
												onClick={() => setIsTimePeriodOpen((prev) => !prev)}
											>
												<span>{timePeriod || "不限"}</span>
												<span className="text-xs text-slate-500">▾</span>
											</button>
											{isTimePeriodOpen && (
												<div className="absolute left-0 right-0 z-10 mt-2 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
													{["不限", "上午", "下午"].map((option) => (
														<button
															key={option}
															type="button"
															className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${timePeriod === option || (!timePeriod && option === "不限")
																? "bg-amber-50 text-amber-700"
																: "text-slate-700 hover:bg-slate-50"
																}`}
															onClick={() => {
																setTimePeriod(option === "不限" ? "" : option);
																setIsTimePeriodOpen(false);
															}}
														>
															<span>{option}</span>
															{(timePeriod === option || (!timePeriod && option === "不限")) && (
																<span className="text-xs font-semibold">✓</span>
															)}
														</button>
													))}
												</div>
											)}
										</div>
									</div>
									<div className="col-span-2 mb-2 mt-2 flex flex-wrap items-center justify-start gap-4">
										<label className="flex items-center gap-2 text-xs text-slate-600">
											<input
												type="checkbox"
												checked={enableMultiDayRun}
												onChange={(e) => {
													setEnableMultiDayRun(e.target.checked);
													setRangeStartValue("");
												}}
												className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
											/>
											选择时段（多天运行）
										</label>
										<label className="flex items-center gap-2 text-xs text-slate-600">
											<input
												type="checkbox"
												checked={showAvoidTimes}
												onChange={(e) => setShowAvoidTimes(e.target.checked)}
												className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
											/>
											显示避开时段
										</label>
										<label className="flex cursor-not-allowed items-center gap-2 text-xs text-slate-400">
											<input
												type="checkbox"
												checked={showRecommendations}
												onChange={(e) => setShowRecommendations(e.target.checked)}
												disabled
												className="h-4 w-4 cursor-not-allowed rounded border-slate-200 text-slate-400 focus:ring-0"
											/>
											显示方位、颜色建议
										</label>
									</div>
								</div>
							</div>
						</details>
					</section>


					{/* 日柱整体吉凶提示 */}
					{!enableMultiDayRun && multiDayResults.map((day) => {
						if (!day.summary.dayGoodLuck || day.summary.dayGoodLuck.length === 0) return null;
						return (
							<div key={`day-good-${day.value}`} className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 shadow-lg shadow-emerald-100/40">
								<div className="flex items-center justify-between">
									<div className="text-base font-bold text-emerald-900">{day.value} 当日吉星</div>
									<span className="rounded-full bg-emerald-200 px-3 py-1 text-xs font-bold text-emerald-800">大吉</span>
								</div>
								{day.summary.dayGoodLuck.map((rule, idx) => (
									<div key={`${day.value}-good-${idx}`} className="mt-2 text-sm text-emerald-800">
										<span className="font-semibold">{rule.name}</span>: {rule.yiji}
									</div>
								))}
							</div>
						);
					})}

					{!enableMultiDayRun && multiDayResults.map((day) => {
						if (!day.summary.dayLuck || day.summary.dayLuck.length === 0) return null;
						return (
							<div key={`day-bad-${day.value}`} className="mt-6 rounded-3xl border border-rose-200 bg-rose-50 p-4 shadow-lg shadow-rose-100/40">
								<div className="flex items-center justify-between">
									<div className="text-base font-bold text-rose-900">{day.value} 当日凶煞</div>
									<span className="rounded-full bg-rose-200 px-3 py-1 text-xs font-bold text-rose-800">大凶</span>
								</div>
								{day.summary.dayLuck.map((rule, idx) => (
									<div key={`${day.value}-bad-${idx}`} className="mt-2 text-sm text-rose-800">
										<span className="font-semibold">{rule.name}</span>: {rule.yiji || rule.description}
									</div>
								))}
							</div>
						);
					})}

					{/* 吉凶时段 */}
					<section className="mt-6 rounded-3xl border border-white/60 bg-white/80 p-4 shadow-lg shadow-orange-100/40">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-lg font-semibold text-slate-900">
									{enableMultiDayRun ? "多天吉凶时段" : "吉凶时段"}
								</h2>
								<p className="text-xs text-slate-600">
									共 {summary.goodCount} 个吉时，{summary.badCount} 个凶时
									{enableMultiDayRun ? `（${selectedDateValues.length} 天）` : ""}
								</p>
							</div>
						</div>

						<div className="mt-4 grid grid-cols-1 gap-4">
							{multiDayResults.map((day) => {
								const visibleItems = day.summary.items.filter((item) => {
									if (!showAvoidTimes) {
										return item.status === "good" || item.status === "mixed";
									}
									return item.good.length > 0 || item.bad.length > 0;
								});

								return (
									<div key={`multi-${day.value}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
										<div className="flex items-center justify-between border-b border-slate-200 pb-2">
											<div>
												<div className="text-sm font-bold text-slate-900">{day.label}</div>
												<div className="text-xs text-slate-600">农历 {day.lunarLabel}</div>
											</div>
											<div className="text-right text-[11px] text-slate-600">
												<div>吉时 {day.summary.goodTimes.length}</div>
												<div>凶时 {day.summary.badTimes.length}</div>
											</div>
										</div>
										<div className="mt-3 grid grid-cols-1 gap-2">
											{visibleItems.map((item) => {
												const statusStyles = {
													good: "border-emerald-200 bg-emerald-50 text-emerald-800",
													bad: "border-rose-200 bg-rose-50 text-rose-800",
													mixed: "border-amber-200 bg-amber-50 text-amber-800",
													neutral: "border-slate-200 bg-white text-slate-700",
												};
												const statusLabel = {
													good: "吉",
													bad: "凶",
													mixed: "次吉",
													neutral: "平",
												};

												return (
													<div
														key={`${day.value}-${item.time}-${item.hour}`}
														className={`rounded-xl border px-3 py-2 shadow-sm ${statusStyles[item.status]}`}
													>
														<div className="flex items-center justify-between">
															<div className="flex items-center gap-2">
																<div className="text-sm font-semibold">{item.time}</div>
																<div className="text-xs opacity-75">({item.hour}时)</div>
															</div>
															<span className="text-xs font-semibold">{statusLabel[item.status]}</span>
														</div>
														{/* {item.good.length > 0 && (
															<div className="mt-2 text-xs text-emerald-800">吉：{item.good.join("、")}</div>
														)} */}
														{/* {item.bad.length > 0 && (
															<div className="mt-1 text-xs text-rose-800">凶：{item.bad.join("、")}</div>
														)} */}
													</div>
												);
											})}
											{visibleItems.length === 0 && (
												<div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
													该日暂无符合当前筛选的时段
												</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</section>
					<div className="mt-6 grid grid-cols-2 gap-4">
						<button
							type="button"
							onClick={() => navigate("/")}
							className="group relative overflow-hidden rounded-2xl border border-white/60 bg-white/40 p-3 shadow-md transition-all hover:bg-white/60 active:scale-95"
						>
							<div className="relative z-10 flex items-center justify-center gap-2 font-semibold text-slate-800">
								<span>圆運排盘</span>
							</div>
							<div className="absolute inset-0 -z-0 bg-gradient-to-br from-amber-100/50 to-orange-100/50 opacity-0 transition-opacity group-hover:opacity-100" />
						</button>

						<button
							type="button"
							onClick={() => navigate("/lunarcalendar")}
							className="group relative overflow-hidden rounded-2xl border border-white/60 bg-white/40 p-3 shadow-md transition-all hover:bg-white/60 active:scale-95"
						>
							<div className="relative z-10 flex items-center justify-center gap-2 font-semibold text-slate-800">
								<span>每日运程</span>
							</div>
							<div className="absolute inset-0 -z-0 bg-gradient-to-br from-amber-100/50 to-orange-100/50 opacity-0 transition-opacity group-hover:opacity-100" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ZeriMobile;
